// JPRO v2 variable definitions — keep in lockstep with model_service/config/profitability_weights.json

export const PROFITABILITY_VARIABLES = [
    { key: 'capital_adequacy',     label: 'Capital Adequacy (CAR)',          range: '-10 to -30', userInput: true,
      docs: ['Audited T-12', 'Audited P&L', 'Reserve Study', 'Operating Budget', 'CNA', 'PNA'] },
    { key: 'recapture_risk',       label: 'Recapture Risk',                  range: '-15 to -25', userInput: true,
      docs: ['IRS 8823', '8823-CORR', 'State MOR', 'Notice of Noncompliance', 'Annual Owner Certification', 'Tenant File Audit'] },
    { key: 'human_capital_roti',   label: 'Human Capital (ROTI)',            range: '+5 to -10',  userInput: true,
      docs: ['Staff Roster', 'Training Log', 'HCCP / SHCM / COS / TaCCs Cert'] },
    { key: 'economic_vacancy',     label: 'Economic Vacancy',                range: '-5 to -15',  userInput: true,
      docs: ['Certified Rent Roll', 'Aged Receivables'] },
    { key: 'defect_density_pdd',   label: 'Physical Defect Density (PDD)',   range: '-10 to -20', userInput: true,
      docs: ['NSPIRE', 'REAC', 'CNA', 'PNA', 'Section 504', 'Fire Marshal', 'PCR', 'Engineering Report', 'Draw Inspection'] },
    { key: 'regulatory_friction',  label: 'Regulatory Friction',             range: '-3 to -7',   userInput: false, docs: [] },
    { key: 'quality_buffer',       label: 'Construction Quality Buffer',     range: '+5 to -10',  userInput: true,
      docs: ['3rd-Party QA Audit', "Architect's Certification", 'Draw Inspection'] },
];

export const AFFORDABILITY_VARIABLES = [
    { key: 'rent_to_ami_burden',     label: 'Rent-to-AMI Burden',        range: '-10 to -30', userInput: true,
      docs: ['Certified Rent Roll', 'TIC', 'HAP Contract', 'Utility Allowance Schedule'] },
    { key: 'habitability_density',   label: 'Habitability Density',      range: '-15 to -25', userInput: true,
      docs: ['NSPIRE', 'REAC', 'CNA', 'PNA', 'Section 504', 'PCR'] },
    { key: 'market_stress',          label: 'Market Stress Index',       range: '-5 to -15',  userInput: false, docs: [] },
    { key: 'tenant_stability_roti',  label: 'Tenant Stability (ROTI)',   range: '+5 to -10',  userInput: true,
      docs: ['Turnover Report', 'Tenant File Audit', 'Eviction Log'] },
    { key: 'oversight_credit',       label: 'Oversight Credit',          range: '+3 to +7',   userInput: false, docs: [] },
    { key: 'affordability_longevity',label: 'Affordability Longevity',   range: '+5 to -10',  userInput: true,
      docs: ['LURA', 'Extended Use Agreement', 'HOME Agreement', 'Deed Restriction'] },
];

export const RATINGS = ['Exceptional', 'Strong', 'Moderate', 'Weak', 'Critical'];

// Color classes for rating chips — green/blue/orange/red, no yellow/amber.
// Matches the TIER_STYLES palette so the language is consistent.
export const RATING_CHIP_STYLES = {
    Exceptional: 'bg-green-500 text-white',
    Strong:      'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
    Moderate:    'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
    Weak:        'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
    Critical:    'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
};

// Always compute rent-to-AMI% from the latest gross_rent + ami_for_unit_size
// when both are present. Doc uploads overwrite proxy gross_rent with the
// property's actual rent — but the proxy's rent_to_ami_pct in raw is stale.
// This helper computes fresh, falls back to stored values otherwise.
export function getRentToAmiPct(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.gross_rent && raw.ami_for_unit_size) {
        return (raw.gross_rent * 12 / raw.ami_for_unit_size) * 100;
    }
    if (raw.rent_to_ami_pct != null) return raw.rent_to_ami_pct;
    if (raw.rent_to_AMI != null) return raw.rent_to_AMI * 100;
    return null;
}

/**
 * Per-variable raw-value formatter — converts Gemini's raw shape into a
 * human-readable explanation shown under the rating chip ("Based on: …").
 * Returns null when the raw doesn't have enough to summarize.
 */
export function formatRawValue(variableKey, raw) {
    if (!raw || typeof raw !== 'object') return null;
    const $ = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

    switch (variableKey) {
        case 'defect_density_pdd': {
            const ls = raw.life_safety_count ?? 0;
            const mj = raw.major_count ?? 0;
            const u  = raw.units ?? 1;
            const d  = u > 0 ? (ls / u) : 0;
            return `${ls} life-safety · ${mj} major · across ${u} units · density ${d.toFixed(3)} LS/unit`;
        }
        case 'habitability_density': {
            const ls = raw.in_unit_life_safety ?? 0;
            const mj = raw.in_unit_major ?? 0;
            const u  = raw.units ?? 1;
            return `${ls} in-unit life-safety · ${mj} in-unit major · across ${u} units`;
        }
        case 'capital_adequacy': {
            const u = raw.unfunded;
            const n = raw.noi;
            if (u != null && n != null && n > 0)
                return `Unfunded ${$(u)} / NOI ${$(n)} = ${(u/n*100).toFixed(1)}% of NOI`;
            if (u != null && n != null) return `Unfunded ${$(u)} · NOI ${$(n)}`;
            if (u != null)              return `Unfunded immediate repairs: ${$(u)} · awaiting NOI`;
            if (n != null)              return `NOI: ${$(n)} · awaiting unfunded repair total`;
            return null;
        }
        case 'recapture_risk': {
            const open = raw.open_findings;
            const hrs  = raw.open_life_safety_hours ?? raw.hours_since_finding;
            const corr = raw.corrected_findings;
            if (open == null && hrs == null && corr == null) return null;
            const parts = [];
            if (open != null) parts.push(`${open} open finding${open === 1 ? '' : 's'}`);
            if (corr != null) parts.push(`${corr} corrected`);
            if (hrs)          parts.push(`oldest open ${hrs}h`);
            return parts.length ? parts.join(' · ') : null;
        }
        case 'rent_to_ami_burden': {
            const pct  = getRentToAmiPct(raw);
            const rent = raw.gross_rent;
            const ami  = raw.ami_for_unit_size;
            if (pct != null && rent && ami) return `Rent ${$(rent)}/mo · AMI ${$(ami)}/yr · burden ${pct.toFixed(1)}% AMI`;
            if (pct != null && rent)        return `Rent ${$(rent)}/mo · burden ${pct.toFixed(1)}% AMI`;
            if (pct != null)                return `Rent at ${pct.toFixed(1)}% of AMI`;
            if (rent && ami)                return `Rent ${$(rent)}/mo · AMI ${$(ami)}/yr`;
            if (rent)                       return `Average gross rent: ${$(rent)}/mo · awaiting AMI from baseline`;
            if (ami)                        return `AMI cap: ${$(ami)}/yr`;
            return null;
        }
        case 'economic_vacancy': {
            const gpr = raw.gross_potential_rent;
            const col = raw.actual_collected;
            // Recompute delta from latest GPR + collected when both present;
            // proxy/older delta_pct may be stale.
            const d = (gpr && col && gpr > 0)
                ? ((gpr - col) / gpr) * 100
                : raw.delta_pct;
            if (d != null && gpr && col) return `GPR ${$(gpr)} vs collected ${$(col)} · delta ${d.toFixed(1)}%`;
            if (d != null)               return `GPR vs collected delta: ${d.toFixed(1)}%`;
            if (gpr && col)              return `GPR ${$(gpr)} vs collected ${$(col)}`;
            if (gpr)                     return `Gross potential rent: ${$(gpr)} · awaiting collected`;
            if (col)                     return `Actual collected: ${$(col)} · awaiting GPR`;
            return null;
        }
        case 'tenant_stability_roti': {
            const t  = raw.turnover_pct;
            const ev = raw.eviction_count;
            const parts = [];
            if (t != null)  parts.push(`Annual turnover: ${t.toFixed(1)}%`);
            if (ev != null) parts.push(`${ev} eviction${ev === 1 ? '' : 's'} on file`);
            return parts.length ? parts.join(' · ') : null;
        }
        case 'human_capital_roti': {
            const t  = raw.staff_turnover_pct;
            const c  = raw.certified_staff;
            const cn = raw.cert_count;
            const parts = [];
            if (t != null)  parts.push(`Staff turnover ${t.toFixed(1)}%`);
            if (c)          parts.push('Certified ProfTech on staff');
            if (cn != null && !c) parts.push(`${cn} active cert${cn === 1 ? '' : 's'}`);
            return parts.length ? parts.join(' · ') : null;
        }
        case 'affordability_longevity':
            return raw.years_remaining != null ? `${raw.years_remaining} years remaining on regulatory agreement` : null;
        case 'quality_buffer':
            if (raw.has_qa_audit === undefined) return null;
            return raw.has_qa_audit ? '3rd-party QA on file' : 'No 3rd-party QA in last 12 months';

        // Proxy-driven (system) variables — also show the figures that drove the rating.
        case 'market_stress': {
            const v = raw.vacancy_band;
            const r = raw.rent_trend;
            if (v && r) return `${v} vacancy band · ${r} rent trend`;
            if (v) return `${v} vacancy band`;
            if (r) return `${r} rent trend`;
            return null;
        }
        case 'oversight_credit':
            return raw.oversight_strength ? `${raw.oversight_strength} state HFA oversight` : null;
        case 'regulatory_friction': {
            const lvl = raw.friction_intensity || raw.intensity;
            const state = raw.state;
            if (lvl && state) return `${lvl} jurisdictional friction (${state})`;
            if (lvl) return `${lvl} jurisdictional friction`;
            return null;
        }
        default:
            return null;
    }
}

/**
 * Client-side rating derivation from a raw payload.
 *
 * Mirrors `_rating_from_raw` in model_service/profitabilityIndex/calculator.py
 * so the UI shows the same rating the server will compute. Without this, a
 * variable extracted with raw numbers but no explicit rating shows "Pending"
 * until the user clicks Calculate — even though the threshold rule already
 * determines the answer.
 */
export function deriveRatingFromRaw(variableKey, raw) {
    if (!raw || typeof raw !== 'object') return null;

    switch (variableKey) {
        case 'capital_adequacy': {
            // Need both NOI (denominator) and unfunded (numerator) to derive.
            if (raw.noi == null || raw.unfunded == null || raw.noi <= 0) return null;
            const pct = raw.unfunded / raw.noi * 100;
            if (pct > 20) return 'Critical';
            if (pct > 10) return 'Weak';
            return 'Strong';
        }
        case 'defect_density_pdd': {
            // Need life-safety count and units to compute density.
            if (raw.life_safety_count == null || !raw.units) return null;
            const density = raw.life_safety_count / raw.units;
            if (density > 0.05) return 'Weak';
            return density === 0 ? 'Strong' : 'Moderate';
        }
        case 'habitability_density': {
            // Need in-unit findings + units to compute.
            if (raw.in_unit_life_safety == null || raw.in_unit_major == null || !raw.units) return null;
            if (raw.in_unit_life_safety > 0) return 'Critical';
            const density = raw.in_unit_major / raw.units;
            if (density > 0.05) return 'Weak';   // tightened from 0.10 — in-unit issues affect tenants directly
            if (density > 0.02) return 'Moderate';
            return raw.in_unit_major === 0 ? 'Strong' : 'Moderate';
        }
        case 'recapture_risk': {
            // Need at least one signal to derive (open hours, open findings, or correction history).
            const hrs  = raw.open_life_safety_hours;
            const open = raw.open_findings;
            const corr = raw.corrected_findings;
            if (hrs == null && open == null && corr == null) return null;
            if ((hrs ?? 0) > 72)   return 'Critical';
            if ((open ?? 0) > 0)   return 'Weak';
            if ((corr ?? 0) > 0)   return 'Moderate';
            return 'Strong';
        }
        case 'economic_vacancy': {
            // Recompute delta from latest GPR + collected when both present;
            // otherwise fall back to stored delta_pct.
            const gpr = raw.gross_potential_rent;
            const col = raw.actual_collected;
            const d = (gpr && col && gpr > 0) ? ((gpr - col) / gpr) * 100 : raw.delta_pct;
            if (d == null) return null;
            if (d > 5) return 'Weak';
            return d < 1 ? 'Strong' : 'Moderate';
        }
        case 'rent_to_ami_burden': {
            const pct = getRentToAmiPct(raw);
            if (pct == null) return null;  // honest "pending" — don't fake a rating
            if (pct > 40) return 'Critical';
            if (pct > 30) return 'Weak';
            return 'Strong';
        }
        case 'tenant_stability_roti': {
            if (raw.turnover_pct == null) return null;
            if (raw.turnover_pct > 25) return 'Weak';
            return raw.turnover_pct < 10 ? 'Strong' : 'Moderate';
        }
        case 'human_capital_roti': {
            // Need at least one signal — staff turnover or cert presence.
            if (raw.staff_turnover_pct == null && raw.certified_staff === undefined && raw.cert_count == null) return null;
            if ((raw.staff_turnover_pct ?? 0) > 30) return 'Weak';
            if (raw.certified_staff || (raw.cert_count ?? 0) > 0) return 'Exceptional';
            return 'Moderate';
        }
        case 'affordability_longevity': {
            if (raw.years_remaining == null) return null;
            if (raw.years_remaining < 5) return 'Weak';
            return raw.years_remaining > 15 ? 'Strong' : 'Moderate';
        }
        case 'quality_buffer':
            // has_qa_audit must be explicitly true/false. If field is absent, no rating.
            if (raw.has_qa_audit === undefined) return null;
            return raw.has_qa_audit ? 'Exceptional' : 'Moderate';
        case 'market_stress': {
            const vac = raw.vacancy_band;
            const trend = raw.rent_trend;
            if (vac === 'High' || trend === 'Falling') return 'Weak';
            if (vac === 'Low' && trend === 'Rising') return 'Strong';
            return 'Moderate';
        }
        case 'oversight_credit':
            if (raw.oversight_strength === 'Strong') return 'Exceptional';
            if (raw.oversight_strength === 'Weak')   return 'Weak';
            return 'Moderate';
        default:
            return null;
    }
}

/**
 * Threshold explanation — which rule landed the variable in this rating.
 * Mirrors the thresholds from profitability_weights.json so the UI matches
 * the calculator's logic exactly.
 */
export function getThresholdNote(variableKey, raw) {
    if (!raw) return null;

    switch (variableKey) {
        case 'capital_adequacy': {
            const noi = raw.noi;
            const u   = raw.unfunded;
            if (noi == null && u == null) return null;
            if (noi == null) return 'Have unfunded repairs — upload Audited T-12 or P&L for NOI';
            if (u == null)   return 'Have NOI — upload CNA, PNA, or Reserve Study for unfunded repairs';
            if (noi <= 0)    return null;
            const pct = (u / noi * 100);
            if (pct > 20) return 'Threshold: CAR > 20% of NOI → Imminent / Critical';
            if (pct > 10) return 'Threshold: CAR 10–20% of NOI → Watch';
            return 'Threshold: CAR < 10% of NOI → Strong';
        }
        case 'defect_density_pdd': {
            if (raw.life_safety_count == null || !raw.units) return null;
            const d = raw.life_safety_count / raw.units;
            if (d > 0.10) return 'Threshold: > 0.10 LS/unit → Critical';
            if (d > 0.05) return 'Threshold: > 0.05 LS/unit → Imminent';
            if (d > 0.02) return 'Threshold: 0.02–0.05 LS/unit → Moderate';
            return 'Threshold: < 0.02 LS/unit → Strong';
        }
        case 'habitability_density': {
            const ls = raw.in_unit_life_safety ?? 0;
            const mj = raw.in_unit_major ?? 0;
            const u = raw.units || 0;
            if (ls > 0) return 'Any in-unit life-safety finding triggers Critical Override';
            if (!u) return null;
            const d = mj / u;
            if (d > 0.05) return 'Threshold: > 0.05 in-unit major/unit → Weak (in-unit conditions affect tenants directly)';
            if (d > 0.02) return 'Threshold: 0.02–0.05 in-unit major/unit → Moderate';
            return 'Threshold: < 0.02 in-unit major/unit → Strong';
        }
        case 'rent_to_ami_burden': {
            const pct = getRentToAmiPct(raw);
            if (pct == null) {
                const rent = raw.gross_rent;
                const ami  = raw.ami_for_unit_size;
                if (rent && !ami)  return 'Have rent — re-run baseline (or upload TIC / HAP) for AMI';
                if (ami && !rent)  return 'Have AMI — upload a Certified Rent Roll for actual rents';
                return 'Need both monthly rent and annual AMI to compute burden %';
            }
            if (pct > 40) return 'Threshold: > 40% AMI → Critical (displacement risk)';
            if (pct > 30) return 'Threshold: > 30% AMI → Watch';
            return 'Threshold: ≤ 30% AMI → Strong';
        }
        case 'recapture_risk': {
            const h = raw.open_life_safety_hours ?? 0;
            if (h > 72) return 'Open life-safety > 72h → forces Critical Override';
            return null;
        }
        case 'economic_vacancy': {
            const gpr = raw.gross_potential_rent;
            const col = raw.actual_collected;
            const d = (gpr && col && gpr > 0) ? ((gpr - col) / gpr) * 100 : raw.delta_pct;
            if (d == null) {
                if (gpr && !col) return 'Have GPR — upload Aged Receivables Report for actual collected';
                if (col && !gpr) return 'Have collected — upload Certified Rent Roll for GPR';
                return 'Need both GPR and actual collected to compute delta';
            }
            if (d > 5) return 'Threshold: > 5% delta → management distress signal';
            return 'Threshold: ≤ 5% delta → Strong';
        }
        case 'tenant_stability_roti': {
            if (raw.turnover_pct == null) return 'Need annual turnover % — upload Turnover Report or Tenant File Audit';
            if (raw.turnover_pct > 25) return 'Threshold: > 25% annual turnover → Weak';
            return 'Threshold: ≤ 25% turnover → Strong / Moderate';
        }
        case 'human_capital_roti': {
            const t = raw.staff_turnover_pct;
            const certified = raw.certified_staff || (raw.cert_count ?? 0) > 0;
            if (t == null && !certified) return 'Need staff turnover % or professional certs — upload Staff Roster, Training Log, or Cert';
            if (t != null && t > 30) return 'Threshold: > 30% staff turnover → Weak';
            if (certified) return 'Certified ProfTech staff → Exceptional bonus';
            return null;
        }
        case 'affordability_longevity': {
            if (raw.years_remaining == null) return 'Need years remaining on regulatory term — upload LURA, Extended Use Agreement, or HOME Agreement';
            if (raw.years_remaining < 5) return 'Threshold: < 5 years on LURA → Watch (displacement risk)';
            if (raw.years_remaining < 15) return 'Threshold: 5–15 years → Moderate';
            return 'Threshold: > 15 years remaining → Strong';
        }
        default:
            return null;
    }
}

export const TIER_STYLES = {
    'Highest Confidence': { bg: 'bg-green-500',  fg: 'text-white',        ring: 'ring-green-500' },
    'Watch':              { bg: 'bg-blue-500',   fg: 'text-white',        ring: 'ring-blue-500' },
    'Imminent':           { bg: 'bg-orange-500', fg: 'text-white',        ring: 'ring-orange-500' },
    'Critical':           { bg: 'bg-red-600',    fg: 'text-white',        ring: 'ring-red-600' },
};

// What each tier means — ordered top → bottom for the legend popover.
// Keep in sync with model_service/config/profitability_weights.json → tiers.
export const TIER_DEFINITIONS = [
    {
        tier: 'Highest Confidence',
        range: '90–100',
        meaning: 'Strong alignment of profitability and affordability — the asset is healthy on both axes.',
        unlock: 'Reaching this band requires verified High-Trust documents. Self-select-only inputs cap at 89.',
    },
    {
        tier: 'Watch',
        range: '70–89',
        meaning: 'Stable today, but early warning signs are showing in velocity or momentum (rent burden creeping up, deferred maintenance, market softness).',
        unlock: 'Most self-select runs land here. Address the worst-impact variables to climb into Highest Confidence.',
    },
    {
        tier: 'Imminent',
        range: '60–69',
        meaning: 'Pressures are likely to impact NOI or compliance within 12 months. Multiple variables are in the deduction zone.',
        unlock: 'Treat 30-day Velocity Plan items as priorities — this tier signals you have a runway, not a guarantee.',
    },
    {
        tier: 'Critical',
        range: '0–59',
        meaning: 'Urgent risk. Either a Tier-1 trigger fired (capital adequacy / habitability / open life-safety > 72h) or the averaged score collapsed.',
        unlock: 'No averaging out of a life-safety crisis. Resolve the Critical Override trigger before the rest of the score matters.',
    },
];

export const PROVENANCE_STYLES = {
    proxy:          { label: 'Proxy',           bg: 'bg-slate-200 dark:bg-slate-700', fg: 'text-slate-700 dark:text-slate-200' },
    self_selected:  { label: 'Self-Selected',   bg: 'bg-blue-100 dark:bg-blue-950',   fg: 'text-blue-700 dark:text-blue-200' },
    doc_verified:   { label: 'Document-Verified', bg: 'bg-green-100 dark:bg-green-950', fg: 'text-green-700 dark:text-green-200' },
};

export const STEPS = [
    { id: 'proxy',   label: 'Baseline',    detail: 'ZIP + Program' },
    { id: 'inputs',  label: 'Inputs',      detail: 'Upload or Self-Select' },
    { id: 'review',  label: 'Review',      detail: 'Logic Gate Check' },
    { id: 'result',  label: 'Result',      detail: 'Score & Velocity Plan' },
];

// Per-variable rating criteria. Each entry is what each rating means for THAT variable.
// Derived from the rule thresholds in model_service/config/profitability_weights.json so
// the UI display stays aligned with the calculator's actual scoring.
// Variables that are system-driven (regulatory_friction, market_stress, oversight_credit)
// are intentionally absent — those are read-only proxy values, no user rating to pick.
export const RATING_CRITERIA = {
    capital_adequacy: {
        unit: '% of NOI',
        Exceptional: 'Reserves fully fund all immediate repairs (CAR ≤ 0)',
        Strong:      'CAR < 10% of NOI',
        Moderate:    'CAR 10–20% of NOI (Watch threshold)',
        Weak:        'CAR 20–30% of NOI (Imminent threshold)',
        Critical:    'CAR > 30% or reserves exhausted',
    },
    recapture_risk: {
        unit: '8823 / MOR history',
        Exceptional: 'No findings on record; corrections current',
        Strong:      'No open findings; minor history fully corrected',
        Moderate:    'Some corrected findings on file; no open issues',
        Weak:        'Open findings (non-LS); correction in progress',
        Critical:    'Open life-safety finding > 72 hours — recapture exposure imminent',
    },
    human_capital_roti: {
        unit: 'staff certs & turnover',
        Exceptional: 'Certified ProfTech staff (HCCP/SHCM/COS); turnover < 15%',
        Strong:      'Mostly certified; turnover < 25%',
        Moderate:    'Mixed certification; turnover 25–30%',
        Weak:        'Few/no certs; turnover > 30%',
        Critical:    'No certified staff; turnover > 40% or key roles vacant',
    },
    economic_vacancy: {
        unit: 'GPR vs collected delta',
        Exceptional: 'Delta < 1% — collections nearly match GPR',
        Strong:      'Delta 1–3%',
        Moderate:    'Delta 3–5%',
        Weak:        'Delta > 5% — signals management distress',
        Critical:    'Delta > 10% — collections systems failing',
    },
    defect_density_pdd: {
        unit: 'life-safety findings per unit',
        Exceptional: '0 life-safety findings; minor items only',
        Strong:      'PDD < 0.02 LS / unit',
        Moderate:    'PDD 0.02–0.05 LS / unit',
        Weak:        'PDD > 0.05 LS / unit (Imminent threshold)',
        Critical:    'PDD > 0.10 LS / unit — widespread deficiencies',
    },
    quality_buffer: {
        unit: '3rd-party QA presence',
        Exceptional: 'Active 3rd-party QA + architect certification on file',
        Strong:      'Recent draw inspections by an independent firm',
        Moderate:    'Periodic QA; no continuous oversight',
        Weak:        'No 3rd-party QA in last 12 months',
        Critical:    'No QA history; unverified rework on capital projects',
    },
    rent_to_ami_burden: {
        unit: 'gross rent vs Area Median Income',
        Exceptional: 'Rent < 25% of AMI',
        Strong:      'Rent 25–30% of AMI',
        Moderate:    'Rent 30–35% of AMI (Watch threshold)',
        Weak:        'Rent 35–40% of AMI (approaching Imminent)',
        Critical:    'Rent > 40% of AMI — displacement risk',
    },
    habitability_density: {
        unit: 'in-unit defects per unit',
        Exceptional: '0 in-unit findings of any severity',
        Strong:      '< 0.02 in-unit major / unit · 0 in-unit life-safety',
        Moderate:    '0.02–0.05 in-unit major / unit · 0 in-unit life-safety',
        Weak:        '> 0.05 in-unit major / unit · 0 in-unit life-safety',
        Critical:    'Any unresolved in-unit life-safety (auto-Critical Override)',
    },
    tenant_stability_roti: {
        unit: 'annual tenant turnover',
        Exceptional: 'Turnover < 10% annually',
        Strong:      'Turnover 10–18%',
        Moderate:    'Turnover 18–25%',
        Weak:        'Turnover > 25%',
        Critical:    'Turnover > 40% — eviction-driven instability',
    },
    affordability_longevity: {
        unit: 'years remaining on LURA / regulatory agreement',
        Exceptional: '> 25 years remaining or LURA recently renewed',
        Strong:      '15–25 years remaining',
        Moderate:    '5–15 years remaining',
        Weak:        '< 5 years (Watch threshold — displacement risk)',
        Critical:    'LURA expiring < 1 year — conversion risk',
    },
};

// Maps the classifier's snake_case document_type to a human label shown in the UI.
export const DOC_TYPE_LABELS = {
    // Financials
    audited_t12:                 'Audited T-12',
    audited_pl:                  'Audited P&L',
    reserve_study:               'Reserve Study',
    operating_budget:            'Operating Budget',
    // Operations / tenant
    certified_rent_roll:         'Certified Rent Roll',
    aged_receivables:            'Aged Receivables',
    tenant_income_certification: 'Tenant Income Certification (TIC)',
    utility_allowance_schedule:  'Utility Allowance Schedule',
    hap_contract:                'HAP Contract',
    tenant_file_audit:           'Tenant File Audit',
    turnover_report:             'Turnover Report',
    eviction_log:                'Eviction Log',
    // Compliance / regulatory
    irs_8823:                    'IRS Form 8823',
    irs_8823_corr:               'IRS Form 8823-CORR',
    state_mor:                   'State MOR',
    notice_of_noncompliance:     'Notice of Noncompliance',
    annual_owner_certification:  'Annual Owner Certification',
    lura:                        'LURA',
    extended_use_agreement:      'Extended Use Agreement',
    home_agreement:              'HOME Agreement',
    deed_restriction:            'Deed Restriction',
    // Staff / training
    staff_roster:                'Staff Roster',
    training_log:                'Training Log',
    professional_certification:  'Professional Certification',
    // Quality
    third_party_qa_audit:        '3rd-Party QA Audit',
    architect_certification:     "Architect's Certification",
    // Physical condition
    nspire:                      'NSPIRE Inspection',
    reac:                        'REAC Inspection',
    cna:                         'Capital Needs Assessment',
    pna:                         'Physical Needs Assessment',
    section_504:                 'Section 504 Assessment',
    fire_marshal:                'Fire Marshal Inspection',
    property_condition_report:   'Property Condition Report',
    engineering_report:          'Engineering Report',
    draw_inspection:             'Draw Inspection',
    unknown:                     'Unknown / Not High-Trust',
};
